import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";

import { useAppDispatch } from "app/hooks";
import { useGetProductQuery } from "services/products";

import { openProductPicture } from "./product-picture.slice";

// Hover-intent delay: a cursor merely passing over the trigger must not fire a
// request. Only a deliberate hover loads the picture.
const HOVER_INTENT_DELAY_MS = 350;

// The picture URL is a presigned S3 URL that expires after ~1h. Refetch when the
// cached product is older than this so the <img> never points at a dead link.
const PICTURE_URL_MAX_AGE_SECONDS = 55 * 60;

// Upper bound on the hover preview. Viewport-relative so it stays on screen on a
// small laptop while filling a desktop generously (~800px at 1920x1080). These
// three values are the knob to turn if the preview should be bigger/smaller.
const PREVIEW_MAX_PX = 800;
const PREVIEW_MAX_VIEWPORT_WIDTH = 0.55;
const PREVIEW_MAX_VIEWPORT_HEIGHT = 0.75;

// Placeholder box while the photo is being fetched/decoded, before its real
// aspect ratio is known.
const PREVIEW_LOADING_WIDTH_PX = 220;
const PREVIEW_LOADING_HEIGHT_PX = 160;

type PreviewSize = { width: number; height: number };

// Scale an image to fit the preview bounds, preserving aspect ratio. The scale is
// deliberately allowed to exceed 1 so a low-resolution upload still fills the
// preview instead of rendering at its own tiny natural size.
export const getPreviewSize = (
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number
): PreviewSize | null => {
  if (!naturalWidth || !naturalHeight) return null;

  const maxWidth = Math.min(
    PREVIEW_MAX_PX,
    viewportWidth * PREVIEW_MAX_VIEWPORT_WIDTH
  );
  const maxHeight = Math.min(
    PREVIEW_MAX_PX,
    viewportHeight * PREVIEW_MAX_VIEWPORT_HEIGHT
  );
  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);

  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale)
  };
};

type ProductPictureIconProps = {
  productId?: number | null;
  hasPicture?: boolean | null;
  sx?: SxProps<Theme>;
};

// "View Photo" affordance for product rows and autocomplete options. Renders
// nothing unless the product actually has a picture (`has_picture` from the API).
//
// Two ways to view, on purpose:
//   - HOVER shows an inline preview in a Popper. A Popper does not trap focus, so
//     the surrounding Autocomplete keeps its input focused and its dropdown open —
//     the user can compare photos across options without losing their search.
//   - CLICK opens the full-size dialog (hosted globally by ProductPictureViewer).
//     This necessarily closes an Autocomplete dropdown, since the dialog takes
//     focus; it is the right affordance in tables, and a deliberate choice elsewhere.
//
// Nothing image-related is requested until one of those happens, and both share the
// same RTK cache entry, so hovering first makes a subsequent click open instantly.
export default function ProductPictureIcon({
  productId,
  hasPicture,
  sx
}: ProductPictureIconProps) {
  const dispatch = useAppDispatch();
  const anchorRef = useRef<HTMLAnchorElement | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [previewSize, setPreviewSize] = useState<PreviewSize | null>(null);

  const { data: product, isFetching } = useGetProductQuery(
    { id: Number(productId) },
    {
      skip: !shouldLoad || !productId,
      refetchOnMountOrArgChange: PICTURE_URL_MAX_AGE_SECONDS
    }
  );

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  if (!productId || !hasPicture) return null;

  // Stop propagation so the trigger works inside autocomplete options and
  // clickable table rows without selecting/activating them.
  const stopEvent = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleMouseEnter = () => {
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => {
      setShouldLoad(true);
      setPreviewOpen(true);
    }, HOVER_INTENT_DELAY_MS);
  };

  const handleMouseLeave = () => {
    clearHoverTimer();
    setPreviewOpen(false);
  };

  // Measured on load rather than guessed: the card is then sized to the photo's
  // own aspect ratio, so `objectFit: contain` has nothing left to letterbox.
  // Re-measured on every hover (the Popper unmounts its children when closed),
  // which also keeps the size correct after a window resize.
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setPreviewSize(
      getPreviewSize(
        naturalWidth,
        naturalHeight,
        window.innerWidth,
        window.innerHeight
      )
    );
  };

  const hasMeasuredPhoto = Boolean(product?.picture && previewSize);

  return (
    <>
      <Link
        component="button"
        type="button"
        ref={anchorRef}
        underline="hover"
        aria-label="View product photo"
        data-testid="product-picture-icon"
        onMouseDown={stopEvent}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(event: React.MouseEvent) => {
          stopEvent(event);
          clearHoverTimer();
          setPreviewOpen(false);
          dispatch(openProductPicture(productId));
        }}
        sx={{
          // Kept well clear of the preceding text so the cursor does not land on
          // it while reading the row.
          ml: "30px",
          flexShrink: 0,
          whiteSpace: "nowrap",
          fontSize: "13px",
          lineHeight: 1.4,
          cursor: "pointer",
          ...sx
        }}
      >
        View Photo
      </Link>
      <Popper
        open={previewOpen}
        anchorEl={anchorRef.current}
        placement="right-start"
        data-testid="product-picture-preview"
        // Above the Autocomplete popper this usually renders inside.
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          // A preview this large will not fit beside the trigger near a viewport
          // edge — let it flip sides and shift rather than render off screen.
          { name: "flip", enabled: true },
          { name: "preventOverflow", options: { padding: 8 } }
        ]}
      >
        <Paper
          elevation={8}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            // Sized to the photo itself once measured, so the card hugs the image
            // and leaves no blank margin around it. Falls back to a small
            // placeholder box only while the photo is still loading.
            width: hasMeasuredPhoto
              ? `${previewSize?.width}px`
              : `${PREVIEW_LOADING_WIDTH_PX}px`,
            height: hasMeasuredPhoto
              ? `${previewSize?.height}px`
              : `${PREVIEW_LOADING_HEIGHT_PX}px`,
            // The preview is display-only; never let it swallow pointer events
            // from the list underneath it.
            pointerEvents: "none"
          }}
        >
          {isFetching || !product ? (
            <CircularProgress
              size={32}
              data-testid="product-picture-preview-loading"
            />
          ) : product.picture ? (
            <Box
              component="img"
              src={product.picture}
              alt="Product photo"
              onLoad={handleImageLoad}
              sx={{
                display: "block",
                // Fill the card rather than capping at the source's natural size:
                // `max-width`/`max-height` only ever shrink an image, so a
                // low-resolution upload would otherwise render at its own tiny
                // size. The card matches the photo's aspect ratio, so `contain`
                // never actually letterboxes — it only guards against a stale
                // measurement for one frame.
                width: "100%",
                height: "100%",
                objectFit: "contain"
              }}
            />
          ) : (
            <Typography variant="caption">No photo available</Typography>
          )}
        </Paper>
      </Popper>
    </>
  );
}
